import type {
  SupportV2MigrationProgress,
  SupportV2MigrationRunProgress,
  SupportV2MigrationStepProgress,
} from "@redotech/admin-sdk/rpc/schema/support/get-support-v2-migration-progress";
import { MigrateTeamToSupportV2Step } from "@redotech/admin-sdk/rpc/schema/support/migrate-team-to-support-v2";
import { SUPPORT_V2_MIGRATION_STEP_OPTIONS as MIGRATE_TEAM_TO_SUPPORT_V2_STEP_OPTIONS } from "@redotech/redo-support-v2-migration/execution/migration-execution";

const numberFormatter = new Intl.NumberFormat();

export const STEP_OPTIONS = MIGRATE_TEAM_TO_SUPPORT_V2_STEP_OPTIONS;
export const ALL_STEPS: readonly MigrateTeamToSupportV2Step[] =
  STEP_OPTIONS.map((o) => o.value);
export const PROGRESS_POLL_INTERVAL_MS = 3000;

const STEP_LABEL_BY_VALUE = new Map<string, string>(
  STEP_OPTIONS.map((option) => [option.value, option.label]),
);

export interface RunErrorBucket {
  count: number;
  label: string;
}

export interface RunErrorDetails {
  buckets: RunErrorBucket[];
  description: string;
  title: string;
}

export const isActiveRunStatus = (
  status: SupportV2MigrationRunProgress["status"] | undefined,
) => status === "pending" || status === "running";

export const isTerminalStepStatus = (
  status: SupportV2MigrationStepProgress["status"],
) =>
  status === "completed" ||
  status === "completed_with_failures" ||
  status === "failed" ||
  status === "skipped";

export const formatProgressDate = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "None";

export const formatStepName = (phase: string) =>
  STEP_LABEL_BY_VALUE.get(phase) ??
  phase
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const formatCount = (value: number) => numberFormatter.format(value);

export const getRunRequestedSteps = (
  run: SupportV2MigrationRunProgress | undefined,
): MigrateTeamToSupportV2Step[] | undefined => {
  if (!run) {
    return undefined;
  }

  const steps: MigrateTeamToSupportV2Step[] = [];
  for (const step of run.requestedSteps) {
    const parsed = MigrateTeamToSupportV2Step.safeParse(step);
    if (parsed.success) {
      steps.push(parsed.data);
    }
  }
  if (steps.length === 0 || steps.length === ALL_STEPS.length) {
    return undefined;
  }
  return steps;
};

export const formatRunStepPlan = (run: SupportV2MigrationRunProgress) => {
  const steps = getRunRequestedSteps(run);
  if (!steps) {
    return "All steps";
  }
  return steps.map(formatStepName).join(", ");
};

export const formatDuration = (start: string | null, end: string | null) => {
  if (!start) {
    return "Not started";
  }

  const startTime = new Date(start).getTime();
  const endTime = end ? new Date(end).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((endTime - startTime) / 1000));
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

export const getProcessedCount = (step: SupportV2MigrationStepProgress) =>
  step.insertedCount + step.updatedCount + step.skippedCount + step.failedCount;

const clampPercent = (value: number) => Math.max(0, Math.min(100, value));

export const getStepPercentage = (step: SupportV2MigrationStepProgress) => {
  if (step.totalCount > 0) {
    return clampPercent(
      Math.round((getProcessedCount(step) / step.totalCount) * 100),
    );
  }
  return isTerminalStepStatus(step.status) ? 100 : 0;
};

export const getOverallPercentage = (
  progress: SupportV2MigrationProgress | undefined,
) => {
  const run = progress?.run;
  if (!run) {
    return 0;
  }
  if (run.status === "completed" || run.status === "completed_with_failures") {
    return 100;
  }

  const totalCount = progress.steps.reduce(
    (sum, step) => sum + step.totalCount,
    0,
  );
  if (totalCount > 0) {
    const processedCount = progress.steps.reduce(
      (sum, step) => sum + getProcessedCount(step),
      0,
    );
    return clampPercent(Math.round((processedCount / totalCount) * 100));
  }

  const requestedStepCount = run.requestedSteps.length || ALL_STEPS.length;
  const completedStepCount = progress.steps.filter((step) =>
    isTerminalStepStatus(step.status),
  ).length;
  return clampPercent(
    Math.round((completedStepCount / requestedStepCount) * 100),
  );
};

export const formatError = (error: unknown) =>
  error instanceof Error ? error.message : String(error);

export const isAlreadyActiveResumeError = (error: unknown) =>
  /current status is (pending|running)/.test(formatError(error));

const formatIssueBucketName = (value: string) =>
  value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

export const parseRunError = (error: string): RunErrorDetails => {
  const message = error.replace(/^SupportV2PreflightBlockedError:\s*/, "");
  const preflightMatch = message.match(
    /^Support v2 migration preflight found (\d+) fatal issues for team ([a-f0-9]+): (.*)$/,
  );

  if (preflightMatch) {
    const [, issueCount, teamId, bucketSummary] = preflightMatch;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const buckets = bucketSummary!
      .split(",")
      .map((bucket) => bucket.trim().match(/^([^:]+):(\d+)$/))
      .filter((match): match is RegExpMatchArray => match !== null)
      .map((match) => ({
        count: Number(match[2]),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        label: formatIssueBucketName(match[1]!),
      }));

    return {
      buckets,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: `${formatCount(Number(issueCount))} fatal issues found for team ${teamId!}.`,
      title: "Preflight blocked migration",
    };
  }

  const namedErrorMatch = error.match(/^([A-Za-z0-9]+Error):\s*(.*)$/);
  if (namedErrorMatch) {
    return {
      buckets: [],
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      description: namedErrorMatch[2]!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      title: namedErrorMatch[1]!.replace(/([a-z])([A-Z])/g, "$1 $2"),
    };
  }

  return { buckets: [], description: error, title: "Last run error" };
};
